'use client';
import { type Cluster, MarkerClusterer } from '@googlemaps/markerclusterer';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { CardLocation } from '@/app/components/shared/dealer/components/shared/card-location';
import styles from '@/app/components/shared/dealer/styles.module.css';
import {
  MAP_DEFAULT,
  SearchType,
} from '@/app/components/shared/dealer/utils/dealer-constant';
import {
  findLocationsWithRange,
  getBoundsZoomLevel,
} from '@/app/components/shared/dealer/utils/dealer-search';
import { autoPlacementPopover } from '@/app/components/shared/dealer/utils/popover-autoplacement';
import type {
  DealerLocation,
  DealerSearch,
} from '@/app/components/shared/dealer/utils/types';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { cn } from '@/app/utils/cn';
import { safeCall } from '@/app/utils/safeCall';
import { useDebounceCallback } from '@/components/hooks/useDebounceCallback';

const MarkerStyles = {
  default: {
    logo: [
      'mx-auto',
      'object-contain',
      'absolute',
      'center-x',
      'center-y',
      styles.markerLogoDefault,
    ],
    marker: [
      'text-white',
      'font-noto-bold',
      'transition-bg',
      'size-7',
      'xl:size-10',
      'bg-red-500',
      'rounded-full',
      'shadow-md',
      'border-2',
      'border-white',
      'hover:bg-red-700',
      'hover:border-transparent',
    ],
  },
  active: {
    logo: ['absolute', 'center-x', 'object-contain', styles.markerActive],
    marker: ['relative'],
    svg: ['w-auto', 'h-full'],
  },
} as const;

type MarkerProps = {
  isSelect: boolean;
  dealer?: DealerSearch;
};

const CustomMarerEvent = 'custom-marker-event';

const Marker = ({ isSelect, dealer }: MarkerProps) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // NOTE: prevent trigger gmp-click
  useEffect(() => {
    const cardEl = cardRef.current;
    if (!cardEl) return;
    const handleClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).getAttribute('data-ga-click')) {
        e.stopPropagation();
      }
    };
    cardEl.addEventListener('click', handleClick);
    return () => cardEl.removeEventListener('click', handleClick);
  }, [cardRef]);

  const handleSelectType = (dealer: DealerSearch, tab: string) => {
    if (!dealer.uid || !tab) return;

    (cardRef.current as HTMLElement).dispatchEvent(
      new CustomEvent(CustomMarerEvent, {
        bubbles: true,
        detail: {
          dealerId: dealer.uid,
          tab: tab,
        },
      })
    );
  };

  return (
    <>
      <div
        className={cn(
          isSelect ? MarkerStyles.active.marker : MarkerStyles.default.marker
        )}
      >
        {!!isSelect && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="56"
            height="80"
            viewBox="0 0 56 80"
            fill="none"
            className="h-full w-auto"
          >
            <path
              d="M28 1C42.9277 1 55 13.0723 55 28C55 34.5489 53.1787 40.7011 49.8037 46.29L49.4727 46.8291L49.4678 46.8379C47.5999 49.8855 45.4313 52.7144 43.2227 55.5791C41.1616 58.2524 39.0636 60.9594 37.2021 63.8486L36.833 64.4287C35.155 67.1064 33.9165 69.625 32.6406 72.3857L32.0908 73.585L32.0898 73.5859C31.8285 74.161 31.5663 74.8675 31.3145 75.4912C31.0503 76.1454 30.7756 76.7691 30.4512 77.3135C29.8064 78.3953 29.0748 79 28 79C26.9252 79 26.1937 78.395 25.543 77.3105C25.2156 76.7649 24.9365 76.1405 24.666 75.4844C24.472 75.0136 24.2719 74.5003 24.0684 74.0283L23.8643 73.5723C22.4193 70.3223 21.0391 67.4806 19.1279 64.4307H19.1289C17.1782 61.3014 14.9365 58.4023 12.7373 55.5498C10.6658 52.8629 8.63045 50.2162 6.84473 47.4004L6.49121 46.835L6.49023 46.834L6.16211 46.2939C2.8214 40.703 1 34.5492 1 28C1 13.0723 13.0723 1 28 1Z"
              fill="#ED1C24"
              stroke="white"
              strokeWidth="2"
            />
          </svg>
        )}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/imgs/dealers/logo-isuzu.svg"
          alt="Isuzu Logo"
          className={cn(
            isSelect ? MarkerStyles.active.logo : MarkerStyles.default.logo
          )}
          style={{ aspectRatio: '44 / 8' }}
        />
      </div>
      {!isSelect && !!dealer && (
        <div
          className="center-x absolute bottom-full hidden pb-2.5 group-hover:block"
          ref={cardRef}
          data-popover
        >
          <CardLocation
            className="font-noto w-96 shadow-sm"
            dealer={dealer}
            onClick={handleSelectType}
          />
        </div>
      )}
    </>
  );
};

interface ClusteredMarkersProps {
  dealers?: DealerSearch[];
  dealerUids?: string;
  selectedDealer?: DealerSearch | null;
  onClickMarker?: (id: string, type?: string) => void;
}

const createMarkerContent = (isSelect = false, dealer?: DealerSearch) => {
  const markerEl = document.createElement('div');
  markerEl.classList.add('relative', 'group');

  createRoot(markerEl).render(<Marker isSelect={isSelect} dealer={dealer} />);

  const handleMouseEnter = (e: MouseEvent) => {
    (e.currentTarget as HTMLElement).parentElement?.classList.add('z-10');
    autoPlacementPopover(markerEl);
  };

  const handleMouseLeave = (e: MouseEvent) => {
    (e.currentTarget as HTMLElement).parentElement?.classList.remove('z-10');
  };

  markerEl.addEventListener('mouseenter', handleMouseEnter);
  markerEl.addEventListener('mouseleave', handleMouseLeave);

  // Attach cleanup so we can call it later
  (markerEl as any)._hoverCleanup = () => {
    markerEl.removeEventListener('mouseenter', handleMouseEnter);
    markerEl.removeEventListener('mouseleave', handleMouseLeave);
  };

  return markerEl;
};

const createClusterMarkerContent = (count: number) => {
  const clusterEl = document.createElement('div');
  clusterEl.classList.add(...MarkerStyles.default.marker);
  if (count) {
    const spanEl = document.createElement('span');
    spanEl.classList.add(
      'absolute',
      'center-x',
      'center-y',
      'flex',
      'items-center',
      styles.markerClusterCount
    );
    spanEl.innerHTML = `${count}`;
    clusterEl.appendChild(spanEl);
  }
  return clusterEl;
};

interface DealerClusteredMarkersProps extends ClusteredMarkersProps {
  currentLocation?: google.maps.LatLngLiteral | null;
  searchType?: SearchType;
  suggestPlaceLocation?: google.maps.LatLngLiteral | null;
  isFromMarker?: boolean;
  onClickCurrentLocation?: () => void;
}

interface PreviousZoom {
  zoom: number;
  center: google.maps.LatLngLiteral;
}

export const ClusteredMarkers = ({
  dealers = [],
  dealerUids,
  selectedDealer,
  searchType,
  isFromMarker,
  currentLocation,
  suggestPlaceLocation,
  onClickMarker,
  onClickCurrentLocation,
}: DealerClusteredMarkersProps) => {
  const map = useMap();
  const isDesktop = useMediaQuery('(min-width: 1280px)');
  const markerLib = useMapsLibrary('marker');

  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);

  const [activeMarker, setActiveMarker] =
    useState<google.maps.marker.AdvancedMarkerElement | null>(null);
  const [isMapInit, setIsMapInit] = useState<boolean>(false);
  const [isInitLocation, setIsInitLocation] = useState<boolean>(false);
  const [prevZoom, setPrevZoom] = useState<PreviousZoom | null>(null);

  useEffect(() => {
    if (!map || isMapInit || !currentLocation || isInitLocation) return;
    const renderingType = map.getRenderingType();
    if (renderingType === 'VECTOR') {
      setIsInitLocation(true);
    }
    safeCall(onClickCurrentLocation);
  }, [currentLocation, isInitLocation, isMapInit, map, onClickCurrentLocation]);

  const initMapCenter = () => {
    if (!map) return;
    const projection = map.getProjection();
    if (!projection) return;
    const centerPoint = projection.fromLatLngToPoint(
      new google.maps.LatLng(MAP_DEFAULT.CENTER)
    );
    const pixelOffset = new google.maps.Point(0, 0);
    if (!centerPoint) return;
    const newPoint = new google.maps.Point(
      centerPoint.x + pixelOffset.x,
      centerPoint.y + pixelOffset.y
    );
    const newCenter = projection.fromPointToLatLng(newPoint);
    if (!newCenter) return;
    map.setCenter(newCenter);
  };

  useEffect(() => {
    if (!map || !isMapInit) return;
    initMapCenter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isMapInit]);

  const createBoundsFromLocations = (
    locations: DealerSearch[] | DealerLocation[]
  ) => {
    const bounds = new google.maps.LatLngBounds();
    for (const location of locations) {
      bounds.extend(new google.maps.LatLng(+location.lat, +location.lng));
    }
    return bounds;
  };

  const withAnimateZoom = (
    map: google.maps.Map,
    zoom: number,
    panTo?: google.maps.LatLngLiteral
  ) => {
    if (!map) return;
    const currentZoom = map.getZoom() || MAP_DEFAULT.ZOOM;
    if (currentZoom >= zoom) return;
    if (currentZoom !== zoom) {
      google.maps.event.addListenerOnce(map, 'zoom_changed', () => {
        withAnimateZoom(map, zoom);
      });
      if (panTo) map.setCenter(panTo);
      setTimeout(function () {
        map.setZoom(currentZoom + 1);
      }, 50);
    }
  };

  const zoomToLocation = useCallback(() => {
    if (!map || activeMarker) return;

    if (dealers.length === 0 && suggestPlaceLocation) {
      const bounds = createBoundsFromLocations([
        suggestPlaceLocation,
      ] as DealerLocation[]);
      map.fitBounds(bounds);

      return;
    }

    const bounds = createBoundsFromLocations(dealers);
    map.fitBounds(bounds);

    if (suggestPlaceLocation) {
      const boundsZoomLevel = getBoundsZoomLevel(bounds);
      const currentZoom = boundsZoomLevel - 1;
      if (dealers.length <= 3) {
        return map.setZoom(currentZoom);
      }
      withAnimateZoom(map, currentZoom, suggestPlaceLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMarker, dealers, map, suggestPlaceLocation]);

  const zoomToCurrentLocation = useCallback(() => {
    if (!map || !currentLocation || searchType === SearchType.SUGGEST) return;
    findLocationsWithRange(dealers, map, currentLocation, isDesktop);
  }, [currentLocation, dealers, isDesktop, map, searchType]);

  useEffect(() => {
    if (!isMapInit) return;
    if (searchType === SearchType.SUGGEST) {
      zoomToLocation();
    } else if (searchType === SearchType.CURRENT_LOCATION) {
      zoomToCurrentLocation();
    }
  }, [isMapInit, searchType, zoomToCurrentLocation, zoomToLocation]);

  const initMarkerClusterer = (
    markers: google.maps.marker.AdvancedMarkerElement[] = []
  ) => {
    clustererRef.current = new MarkerClusterer({
      map,
      markers,
      onClusterClick: (
        _: google.maps.MapMouseEvent,
        cluster: Cluster,
        map: google.maps.Map
      ) => {
        if (!cluster?.bounds) return;
        const boundsZoomLevel = getBoundsZoomLevel(cluster?.bounds);
        withAnimateZoom(map, boundsZoomLevel - 1);
        map.panTo(cluster?.bounds.getCenter());
        map.getDiv().focus();
      },
      renderer: {
        render: ({ position, count }) => {
          return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: createClusterMarkerContent(count),
          });
        },
      },
    });
  };

  const refreshMarkerClusterer = (
    markers: google.maps.marker.AdvancedMarkerElement[] = []
  ) => {
    if (!clustererRef.current) return;
    clustererRef.current.clearMarkers();
    clustererRef.current.addMarkers(markers);
  };

  const cleanupMarkerContent = (content: any) => {
    safeCall(content._hoverCleanup);
  };

  const renderAllMarkers = useDebounceCallback(() => {
    if (!markerLib) return;

    const markers = dealers.map((item) => {
      const isActive = selectedDealer?.uid === item.uid;
      const marker = new markerLib.AdvancedMarkerElement({
        position: {
          lat: +item?.lat || 0,
          lng: +item?.lng || 0,
        },
        content: createMarkerContent(isActive, item),
      });
      marker.setAttribute('uid', item.uid);
      marker.addEventListener(CustomMarerEvent, (e: CustomEvent) => {
        safeCall(onClickMarker, e.detail.dealerId, e.detail.tab);
      });
      marker.addListener('gmp-click', () => {
        safeCall(onClickMarker, item.uid);
      });

      if (isActive) {
        setActiveMarker(marker);
      }
      return marker;
    });
    markersRef.current = markers;
    if (!!clustererRef.current) {
      refreshMarkerClusterer(markers);
    } else {
      initMarkerClusterer(markers);
    }
  }, 500);

  const renderActiveMarker = (dealer: DealerSearch) => {
    if (markersRef.current?.length === 0) return;

    const newActiveMarker = markersRef.current.find(
      (item) => item.getAttribute('uid') === dealer.uid
    );
    if (!newActiveMarker) return;

    if (activeMarker) {
      cleanupMarkerContent(activeMarker.content);
      const activeDealer = dealers.find(
        (d) => d.uid === activeMarker.getAttribute('uid')
      );

      if (activeDealer) {
        activeMarker.content = createMarkerContent(false, activeDealer);
      }
    }

    cleanupMarkerContent(newActiveMarker.content);
    newActiveMarker.content = createMarkerContent(true, dealer);
    setActiveMarker(newActiveMarker);
  };

  const removeActiveMarker = () => {
    if (!activeMarker) return;
    cleanupMarkerContent(activeMarker.content);
    const dealer = dealers.find(
      (d) => d.uid === activeMarker.getAttribute('uid')
    );
    activeMarker.content = createMarkerContent(false, dealer);

    setActiveMarker(null);

    if (map && prevZoom) {
      if (isFromMarker) {
        map.setCenter(prevZoom.center);
        map.setZoom(prevZoom.zoom);
      } else {
        map.setZoom(MAP_DEFAULT.ZOOM);
        map.setCenter(MAP_DEFAULT.CENTER);
      }
    }
  };

  const setActiveMarkerHandler = (
    map: google.maps.Map,
    marker?: google.maps.marker.AdvancedMarkerElement
  ) => {
    if (!marker || searchType === SearchType.CURRENT_LOCATION) return;
    cleanupMarkerContent(marker.content);
    const dealer = dealers.find((d) => d.uid === marker.getAttribute('uid'));

    if (!dealer) return;

    setPrevZoom({
      zoom: map.getZoom() || MAP_DEFAULT.ZOOM_IN,
      center: {
        lat: map.getCenter()?.lat() || MAP_DEFAULT.CENTER.lat,
        lng: map.getCenter()?.lng() || MAP_DEFAULT.CENTER.lng,
      },
    });

    marker.content = createMarkerContent(true, dealer);

    withAnimateZoom(map, MAP_DEFAULT.ZOOM_SELECTED_DEALER);
    map.panTo({ lat: +dealer?.lat || 0, lng: +dealer?.lng || 0 });
  };

  const init = () => {
    if (!map || !markerLib) {
      return;
    }
    renderAllMarkers();
    if (!isMapInit) {
      setIsMapInit(true);
    }
  };

  // Map camera position handler
  useEffect(() => {
    if (!map || !isMapInit || !activeMarker) return;
    setActiveMarkerHandler(map, activeMarker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMarker, map, isMapInit]);

  // When unselect dealer
  useEffect(() => {
    if (selectedDealer) {
      renderActiveMarker(selectedDealer);
    } else {
      removeActiveMarker();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDealer]);

  // When dealers changed (filtering)
  useEffect(() => {
    if (!isMapInit) return;
    renderAllMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerUids]);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, markerLib]);

  useEffect(() => {
    return () => {
      // Clean up all marker event listeners
      if (markersRef.current) {
        for (const marker of markersRef.current) {
          cleanupMarkerContent(marker.content);
        }
      }
    };
  }, []);

  return null;
};
